import React, { useState, useEffect } from 'react';
import { supervisoryAPI, lookupsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SearchableCombobox } from '../SearchableCombobox';
import { ShieldCheck, Trash2, UserPlus } from 'lucide-react';

/**
 * Attach reusable supervisory groups to a client account, plus any one-off
 * people who only cover this account. Everyone in the merged list gets access
 * to every employee assigned to this client.
 */
export default function ClientTeamDialog({ open, onOpenChange, client, canEdit }) {
  const { user } = useAuth();
  // Attaching groups / editing the chain of command is admin + manager only.
  const canManage = ['admin', 'manager'].includes(user?.role);
  const [levels, setLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [extras, setExtras] = useState([]);
  const [effective, setEffective] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [pickEmp, setPickEmp] = useState('');
  const [pickLevel, setPickLevel] = useState('supervisor');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [t, g, d] = await Promise.all([
          supervisoryAPI.getClientTeam(client.id),
          supervisoryAPI.listGroups({ is_active: true }).catch(() => ({ data: [] })),
          lookupsAPI.employees({ is_active: true }),
        ]);
        setLevels(t.data.levels || []);
        setGroupIds(t.data.supervisory_group_ids || []);
        setExtras((t.data.supervisory_extra_members || []).map(m => ({ ...m })));
        setEffective(t.data.effective_members || []);
        setEmployeeCount(t.data.employee_count || 0);
        setGroups(g.data || []);
        setDirectory(d.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load supervisory team');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, client?.id]);

  const save = async () => {
    if (loading) return;  // never overwrite a team with a half-loaded form
    setSaving(true);
    try {
      const { data } = await supervisoryAPI.saveClientTeam(client.id, {
        supervisory_group_ids: groupIds,
        supervisory_extra_members: extras.map(m => ({ employee_id: m.employee_id, level: m.level })),
      });
      setEffective(data.effective_members || []);
      toast.success('Supervisory team saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addExtra = () => {
    if (!pickEmp) { toast.error('Pick an employee first'); return; }
    const e = directory.find(x => x.id === pickEmp);
    setExtras(prev => [...prev.filter(m => m.employee_id !== pickEmp), {
      employee_id: pickEmp, level: pickLevel, name: e ? `${e.first_name} ${e.last_name}` : pickEmp,
    }]);
    setPickEmp('');
  };

  const pickerItems = directory
    .filter(e => !extras.some(m => m.employee_id === e.id))
    .map(e => ({
      _key: e.id,
      _searchStr: `${e.first_name} ${e.last_name} ${e.employee_number || ''} ${e.position || ''}`.toLowerCase(),
      ...e,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-500" />
            Supervisory team — {client?.name}
          </DialogTitle>
          <DialogDescription>
            Everyone below gets full evaluation access to the {employeeCount} employee(s) assigned to this
            client. Attach a group to reuse a chain of command across accounts.
          </DialogDescription>
        </DialogHeader>

        {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Attached teams</Label>
              {canManage ? (
                <div className="mt-2">
                  <SearchableCombobox
                    items={groups
                      .filter(g => !groupIds.includes(g.id))
                      .map(g => ({
                        _key: g.id,
                        _searchStr: `${g.name} ${(g.members || []).map(m => m.name).join(' ')}`.toLowerCase(),
                        ...g,
                      }))}
                    value=""
                    onSelect={(v) => v && setGroupIds(prev => [...prev, v])}
                    placeholder="Search and attach a supervisory team..."
                    searchPlaceholder="Team name or member..."
                    emptyText={groups.length === 0
                      ? 'No teams yet — create one under Supervisory Groups'
                      : 'No matching teams'}
                    testId="client-team-group-picker"
                    renderItem={(g) => (g ? `${g.name} (${(g.members || []).length} member${(g.members || []).length === 1 ? '' : 's'})` : null)}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-2">
                  Only system administrators and managers can attach supervisory teams.
                </p>
              )}
              {groupIds.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="client-team-attached-list">
                  {groupIds.map(gid => {
                    const g = groups.find(x => x.id === gid);
                    return (
                      <div key={gid} data-testid={`client-team-group-${gid}`}
                        className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{g ? g.name : gid}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {g ? ((g.members || []).map(m => `${m.name} (${m.level_label})`).join(', ') || 'No members') : 'Team unavailable'}
                          </p>
                        </div>
                        {canManage && (
                          <Button size="icon" variant="ghost" data-testid={`client-team-group-remove-${gid}`}
                            onClick={() => setGroupIds(prev => prev.filter(x => x !== gid))}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Account-only members</Label>
              {canManage && (
                <div className="flex flex-col md:flex-row items-stretch md:items-end gap-3 mt-2">
                  <div className="flex-1">
                    <SearchableCombobox
                      items={pickerItems}
                      value={pickEmp}
                      onSelect={(v) => setPickEmp(v || '')}
                      placeholder="Search employees…"
                      searchPlaceholder="Name or employee #"
                      emptyText="No employees found"
                      testId="client-team-member-picker"
                      renderItem={(e) => (e ? `${e.first_name} ${e.last_name}${e.position ? ` — ${e.position}` : ''}` : null)}
                    />
                  </div>
                  <div className="w-full md:w-60">
                    <Select value={pickLevel} onValueChange={setPickLevel}>
                      <SelectTrigger data-testid="client-team-level-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addExtra} data-testid="client-team-add-member-btn">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Add
                  </Button>
                </div>
              )}
              {extras.length > 0 && (
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow><TableHead>Name</TableHead><TableHead className="w-60">Level</TableHead><TableHead className="w-12" /></TableRow>
                  </TableHeader>
                  <TableBody>
                    {extras.map(m => (
                      <TableRow key={m.employee_id} data-testid={`client-team-extra-${m.employee_id}`}>
                        <TableCell className="font-medium">{m.name || m.employee_id}</TableCell>
                        <TableCell>
                          <Select value={m.level} disabled={!canManage}
                            onValueChange={(v) => setExtras(prev => prev.map(x => x.employee_id === m.employee_id ? { ...x, level: v } : x))}>
                            <SelectTrigger data-testid={`client-team-extra-level-${m.employee_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          {canManage && (
                            <Button size="icon" variant="ghost" data-testid={`client-team-extra-remove-${m.employee_id}`}
                              onClick={() => setExtras(prev => prev.filter(x => x.employee_id !== m.employee_id))}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Who currently covers this account</p>
              <p className="text-xs text-slate-500 mb-2">
                Everyone from the attached teams plus any account-only members, merged and deduped. This is
                what the server has stored — it refreshes when you save.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="client-team-effective">
                {effective.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    Nobody yet — attach a team above or add an account-only member, then Save team.
                  </p>
                ) : effective.map(m => (
                  <Badge key={m.employee_id} variant="secondary" className="font-normal">
                    {m.name} — {m.level_label}
                    {m.source !== 'client' && <span className="ml-1 text-slate-400">via {m.source.replace('group:', '')}</span>}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {canManage && (
            <Button onClick={save} disabled={saving || loading} data-testid="client-team-save-btn">
              {saving ? 'Saving…' : 'Save team'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
